import { SubmitHandler, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { DatePicker, VISUAL_DATE_PLACEHOLDER } from "@/components/ui/date-picker"
import { FormField } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { DateOnly } from "@/lib/date-only"
import {
  BodyMeasurementSchemaIn,
  BodyMeasurementSchemaOut,
} from "@/modules/patients/detail/health-information/body-measurement/body-measurements-drawer"

interface BodyMeasurementFormProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSubmit"> {
  serverDate: DateOnly
  onSubmit: SubmitHandler<BodyMeasurementSchemaOut>
  onClear: () => void
  isEditing?: boolean
  isHeightRequired: boolean
}

const regexIntegerNum = /^[0-9]{0,3}$/
const regexDecimalNum = /^(([0-9]{0,3})|([0-9]{1,3}\.[0-9]{0,3}))$/
const minimumDate = new DateOnly("1900-01-01")

const BodyMeasurementForm = ({
  serverDate,
  onClear,
  onSubmit,
  isEditing,
  isHeightRequired,
}: BodyMeasurementFormProps) => {
  const {
    control,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useFormContext<BodyMeasurementSchemaIn, unknown, BodyMeasurementSchemaOut>()
  const { t } = useTranslation()

  const handleSelect = (date: Date) => {
    const selectedDate = new DateOnly(date)
    setValue("date", selectedDate, { shouldDirty: true })
  }

  const onHeightChange = (value: string) => {
    if (!regexIntegerNum.test(value)) {
      return
    }
    setValue("height", value, { shouldDirty: true })
    trigger("height")
  }

  const onWeightChange = (value: string) => {
    if (!regexDecimalNum.test(value)) {
      return
    }
    setValue("weight", value, { shouldDirty: true })
    trigger("weight")
  }

  return (
    <div className="grid content-start gap-4 pt-2 pb-4.25 transition-all">
      <div className="grid grid-cols-3 gap-4">
        <FormField
          name="date"
          control={control}
          render={({ field: { value } }) => {
            return (
              <DatePicker
                disabledMatcher={(date) => !new DateOnly(date).isWithinIncludingRange(minimumDate, serverDate)}
                readOnly={isEditing}
                disabled={isEditing}
                data-testid="date-picker"
                placeholder={VISUAL_DATE_PLACEHOLDER}
                value={value.toDate()}
                today={serverDate.toDate()}
                label={t("patient.healthInformation.bodyMeasurements.date")}
                required
                className="h-12 w-full"
                enableReset={false}
                onSelect={handleSelect}
                error={errors.date?.message}
                translateError
              />
            )
          }}
        />
        <FormField
          name="height"
          control={control}
          render={({ field: { value } }) => {
            return (
              <Input
                className="pr-9"
                testId="height-input"
                error={errors.height?.message}
                translateError
                onChange={(e) => {
                  onHeightChange(e.target.value)
                }}
                value={value ?? ""}
                label={t("patient.healthInformation.bodyMeasurements.measurements.height")}
                required={isHeightRequired}
                type="text">
                <span className="text-on-surface-subdued absolute top-3.5 right-4 w-5 text-left text-sm">
                  {t("patient.healthInformation.bodyMeasurements.measurements.heightUnit")}
                </span>
              </Input>
            )
          }}
        />
        <FormField
          name="weight"
          control={control}
          render={({ field: { value } }) => {
            return (
              <Input
                className="pr-9"
                testId="weight-input"
                error={errors.weight?.message}
                translateError
                value={value ?? ""}
                onChange={(e) => {
                  onWeightChange(e.target.value)
                }}
                label={t("patient.healthInformation.bodyMeasurements.measurements.weight")}
                required
                type="text">
                <span className="text-on-surface-subdued absolute top-3.5 right-4 w-5 text-left text-sm">
                  {t("patient.healthInformation.bodyMeasurements.measurements.weightUnit")}
                </span>
              </Input>
            )
          }}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClear} data-testid="body-measurement-reset-button">
          {isEditing ? t("common.button.cancel") : t("common.button.reset")}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            FaroInsightsWrapper.pushEvent("healthInformation_bodyMeasurements_add")
            handleSubmit(onSubmit)()
          }}
          data-testid="body-measurement-add-button">
          {isEditing ? t("common.button.save") : t("common.add")}
        </Button>
      </div>
    </div>
  )
}

export { BodyMeasurementForm }
